import { useState, useEffect } from "react";
import { AuditFormData } from "@/utils/schemas/formSchemas";

type SubmissionStatus = {
  type: "success" | "error" | null;
  message: string | null;
  trackerId?: string | null; // Add trackerId to SubmissionStatus
  reportId?: string | null;
};

type UseFormSubmissionResult = {
  isLoading: boolean;
  submissionStatus: SubmissionStatus;
  submitForm: (
    data: AuditFormData & { turnstileToken?: string },
    formId: string,
    successMessage?: string,
  ) => Promise<{ success: boolean; trackerId?: string; reportId?: string }>;
  setSubmissionStatus: React.Dispatch<React.SetStateAction<SubmissionStatus>>;
};

export const useFormSubmission = (): UseFormSubmissionResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>({
    type: null,
    message: null,
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (submissionStatus.message) {
      timeoutId = setTimeout(() => {
        setSubmissionStatus({ type: null, message: null });
      }, 7000); // Clear message after 7 seconds
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [submissionStatus.message]);

  const submitForm = async (
    data: AuditFormData & { turnstileToken?: string },
    formId: string,
    successMessage?: string,
  ): Promise<{ success: boolean; trackerId?: string; reportId?: string }> => {
    setIsLoading(true);
    setSubmissionStatus({ type: null, message: null }); // Clear previous status

    try {
      const response = await fetch("/api/forms/submit-and-fetch", {
        method: "POST",
        body: JSON.stringify({
          ...data,
          mailchimpKey: data.apiKey,
          formId: formId,
          sourceUrl: typeof window !== "undefined" ? window.location.href : "",
          turnstileToken: data.turnstileToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Submission failed. Please try again.";
        let errorTrackerId: string | undefined;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          errorTrackerId = errorData.trackerId;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        setSubmissionStatus({
          type: "error",
          message: errorMessage,
          trackerId: errorTrackerId,
        });
        return { success: false, trackerId: errorTrackerId }; // Indicate failure
      }

      const responseData = await response.json();
      const { trackerId, reportId } = responseData;

      setSubmissionStatus({
        type: "success",
        message: successMessage || "Submission successful!",
        trackerId: trackerId,
        reportId: reportId,
      });
      return { success: true, trackerId: trackerId, reportId: reportId }; // Indicate success and return trackerId
    } catch (error: any) {
      setSubmissionStatus({
        type: "error",
        message: error.message || "An error occurred. Please try again.",
      });
      return { success: false }; // Indicate failure
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, submissionStatus, submitForm, setSubmissionStatus };
};
